import { useSetAtom } from "jotai";
import { outlineAtom, partialOutlineAtom } from "../atoms/outlineAtom";
import { zslAtom } from "@zapier/canvas-state";

import { useStreamingCompletion } from "./useStreamingCompletion";

type Options = {
  api: string;
  onComplete: (zsl: any) => void;
  onError: (error: string) => void;
  onMessage?: (msg: any) => void;
};

/**
 * This hook is used to sync the ZSL stream to the outline jotai atom. It has no side effects to update the canvas.
 */

export function useSyncZslStreamToOutline({
  api,
  onComplete,
  onError,
  onMessage,
}: Options) {
  const setZsl = useSetAtom(zslAtom);
  const setOutline = useSetAtom(outlineAtom);
  const setPartialOutline = useSetAtom(partialOutlineAtom);

  const { completion, handleSubmit, triggerRequest } = useStreamingCompletion({
    api,
    onMessage(msg) {
      onMessage?.(msg);
      switch (msg.event) {
        case "partial-outline": {
          const nextChunk = msg.data.slice(1, -1);
          setPartialOutline((prev) => prev + nextChunk);
          break;
        }
        case "outline": {
          setOutline(msg.data);
          break;
        }
        // This is the case where the ZSL is returned
        case "decompressed": {
          const parsedZsl = JSON.parse(msg.data);
          setZsl(parsedZsl);
          onComplete(parsedZsl);
          break;
        }
        case "error":
          // throw an exception so it gets handled by the onerror callback below:
          // throw new Error(msg.data);
          onError(msg.data);
          break;
        default:
          break;
      }
    },
    onError(err) {
      // TODO: handle error
      console.error(err);
    },
  });

  return {
    completion,
    handleSubmit,
    triggerRequest,
  };
}
